import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "../../components/ui/Icon.js";
import { loginRequest, registerRequest } from "../../lib/auth.js";

export function SignUpPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    const reg = await registerRequest(email, password);
    if (!reg.ok) {
      setSubmitting(false);
      setError(
        reg.error === "email_taken"
          ? "An account with that email already exists."
          : reg.error,
      );
      return;
    }
    const login = await loginRequest(email, password);
    setSubmitting(false);
    if (login.ok) {
      navigate("/onboarding", { replace: true });
    } else {
      setError("Account created — please sign in.");
    }
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        overflow: "hidden",
        background: "var(--bg)",
      }}
    >
      <div
        style={{
          flex: 1,
          background:
            "radial-gradient(ellipse at 30% 40%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 70%, rgba(168, 85, 247, 0.12) 0%, transparent 50%), var(--bg)",
          borderRight: "1px solid var(--border)",
          padding: 48,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="se-logo-mark" style={{ width: 28, height: 28 }}>
            <span style={{ fontSize: 15 }}>S</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>SpotEdge</div>
        </div>
        <div style={{ marginTop: "auto", maxWidth: 460 }}>
          <h1
            style={{
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: "-0.025em",
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            Create your SpotEdge account
          </h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 14, lineHeight: 1.55 }}>
            14-day free trial of Pro. No credit card. Connect any of 8 exchanges
            with read-only API keys to sync your spot portfolio.
          </p>
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 32 }}>
          By signing up you agree to our terms of service.
        </div>
      </div>

      <div
        style={{
          width: 460,
          padding: "64px 56px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Sign up</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 6, marginBottom: 28 }}>
          Get started in under a minute.
        </p>
        <form onSubmit={onSubmit}>
          <label style={{ fontSize: 11, color: "var(--text-mid)", display: "block", marginBottom: 4 }}>
            Email
          </label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Icon
              name="mail"
              size={14}
              style={{ position: "absolute", left: 10, top: 11, color: "var(--text-faint)" }}
            />
            <input
              className="se-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
          </div>

          <label style={{ fontSize: 11, color: "var(--text-mid)", display: "block", marginBottom: 4 }}>
            Password
          </label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Icon
              name="lock"
              size={14}
              style={{ position: "absolute", left: 10, top: 11, color: "var(--text-faint)" }}
            />
            <input
              className="se-input"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
          </div>

          <label style={{ fontSize: 11, color: "var(--text-mid)", display: "block", marginBottom: 4 }}>
            Confirm password
          </label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Icon
              name="lock"
              size={14}
              style={{ position: "absolute", left: 10, top: 11, color: "var(--text-faint)" }}
            />
            <input
              className="se-input"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
          </div>

          {error ? (
            <div
              style={{
                marginBottom: 12,
                padding: "8px 10px",
                background: "var(--sell-soft)",
                color: "var(--sell)",
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="se-btn se-btn-primary"
            style={{ width: "100%", padding: "11px 0", fontWeight: 600 }}
          >
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>

        <div className="muted" style={{ fontSize: 12, marginTop: 24, textAlign: "center" }}>
          Already have an account?{" "}
          <Link to="/signin" style={{ color: "var(--primary)" }}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
